// src/resources/customerLifecycle/LifecycleRangeSlider.tsx
//
// Régua "dias sem abastecer" com os dois cortes do ciclo de vida: onde o cliente entra em
// risco e onde fica inativo. A régua nunca devolve par inválido: sempre risco < inativo, com
// pelo menos 1 dia entre os dois. Pra valor exato, os números da legenda são campos editáveis.
//
// Escala não linear: metade da régua cobre 1–90 dias, onde a rede decide; o resto vai até
// 730. Acima de 730 só pelo campo numérico (o ponto fica encostado no fim da régua).
import React, { useEffect, useRef, useState } from 'react';
import { Box, Slider, TextField, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { __t } from '../../i18n/t';

type SegmentKey = 'active' | 'at_risk' | 'inactive';

interface Props {
  atRiskDays: number;
  inactiveDays: number;
  /** Chamado ao soltar o ponto (não a cada pixel): resumo e tabela buscam uma vez só. */
  onCommit: (atRiskDays: number, inactiveDays: number) => void;
  segments: Record<SegmentKey, { label: string; color: string }>;
}

const SLIDER_MAX_DAYS = 730;
// [posição na régua (0–1000), dias] — trechos lineares entre os pontos.
const SCALE: Array<[number, number]> = [[0, 1], [500, 90], [800, 365], [1000, SLIDER_MAX_DAYS]];
const LABELED_DAYS = new Set([30, 60, 90, 180, 365, 730]);

const daysToPos = (days: number): number => {
  if (days <= SCALE[0][1]) return SCALE[0][0];
  for (let i = 1; i < SCALE.length; i++) {
    const [p0, d0] = SCALE[i - 1];
    const [p1, d1] = SCALE[i];
    if (days <= d1) return p0 + ((days - d0) / (d1 - d0)) * (p1 - p0);
  }
  return SCALE[SCALE.length - 1][0];
};

const posToDays = (pos: number): number => {
  for (let i = 1; i < SCALE.length; i++) {
    const [p0, d0] = SCALE[i - 1];
    const [p1, d1] = SCALE[i];
    if (pos <= p1) return Math.round(d0 + ((pos - p0) / (p1 - p0)) * (d1 - d0));
  }
  return SLIDER_MAX_DAYS;
};

const MARKS = [...LABELED_DAYS].map((days) => ({ value: daysToPos(days), label: String(days) }));

// Teclado tratado aqui, em dias: no começo da régua 1 dia ocupa ~6 unidades, então o passo
// do MUI (1 unidade) não saía do lugar. Setas = 1 dia (10 com Shift), PageUp/PageDown = 30,
// Home/End = limite do ponto.
const KEY_STEP_DAYS: Record<string, number> = {
  ArrowRight: 1, ArrowUp: 1, ArrowLeft: -1, ArrowDown: -1, PageUp: 30, PageDown: -30,
};

// Texto em filho JSX (não em objeto de configuração): assim o plugin de i18n do build
// embrulha a frase e o catálogo es casa "até %{arg0} dias". Os pedaços ficam em flex com gap,
// sem espaço entre texto e campo: o build come espaço colado em elemento.
const LegendItem: React.FC<{ label: string; color: string; children: React.ReactNode }> = ({ label, color, children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
    <Typography variant="body2" sx={{ fontWeight: 700, color, whiteSpace: 'nowrap' }}>{label}</Typography>
    {children}
  </Box>
);

const LegendText: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{children}</Typography>
);

// Número editável dentro da legenda. `variant`/`margin` explícitos: o tema do admin (react-admin)
// deixa TextField em `filled` com margem, o que desalinhava o campo do texto em volta.
// Digitando, espera 600 ms parado pra mandar (resumo e tabela buscam uma vez, não a cada tecla);
// Enter ou sair do campo mandam na hora, e valor vazio/inválido volta pro último bom.
const DayInput: React.FC<{ value: number; color: string; ariaLabel: string; onCommit: (days: number) => void }> = ({
  value, color, ariaLabel, onCommit,
}) => {
  const [text, setText] = useState(String(value));
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;

  // Valor mudou por fora (arrasto na régua): o campo acompanha.
  useEffect(() => { setText(String(value)); }, [value]);

  const parse = (raw: string) => {
    const days = Number(raw);
    return raw !== '' && Number.isInteger(days) && days >= 1 ? days : null;
  };

  useEffect(() => {
    const days = parse(text);
    if (days == null || days === value) return undefined;
    const timer = setTimeout(() => commitRef.current(days), 600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  const flush = () => {
    const days = parse(text);
    if (days == null) setText(String(value));
    else if (days !== value) commitRef.current(days);
  };

  // Caixa com largura fixa por fora: o tema do react-admin força `width: 100%` em todo TextField
  // em tela pequena (variants do MuiTextField), passando por cima do sx do próprio campo.
  return (
    <Box sx={{ width: 72, flexShrink: 0 }}>
    <TextField
      type="number"
      variant="outlined"
      size="small"
      margin="none"
      fullWidth
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={flush}
      onKeyDown={(e) => { if (e.key === 'Enter') flush(); }}
      slotProps={{
        htmlInput: {
          min: 1,
          'aria-label': ariaLabel,
          style: { textAlign: 'center', fontWeight: 700, padding: '4px 6px' },
        },
      }}
      sx={{
        bgcolor: 'background.paper',
        borderRadius: 1,
        '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(color, 0.5) },
        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: color },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: color },
      }}
    />
    </Box>
  );
};

const LifecycleRangeSlider: React.FC<Props> = ({ atRiskDays, inactiveDays, onCommit, segments }) => {
  // Enquanto arrasta só a régua anda; o valor vai pra tela quando solta.
  const [draft, setDraft] = useState<[number, number] | null>(null);
  const draftRef = useRef<[number, number] | null>(null);
  const [risk, inactive] = draft ?? [atRiskDays, inactiveDays];
  // O MUI ordena os valores da régua; com "inativo" digitado menor que "risco" (estado
  // inválido, a tela mostra o aviso), a régua lê os dois em ordem e o arrasto já conserta.
  const low = Math.min(risk, inactive);
  const high = Math.max(risk, inactive);

  const handleChange = (_: Event, value: number | number[], activeThumb: number) => {
    if (!Array.isArray(value)) return;
    const [curLow, curHigh] = draftRef.current ?? [Math.min(atRiskDays, inactiveDays), Math.max(atRiskDays, inactiveDays)];
    // O ponto parado volta com a mesma posição que a régua recebeu: mantém o valor exato
    // (inclusive acima de 730, que a régua não representa). Se mudou, os pontos se cruzaram
    // e trocaram de papel: o parado virou o outro corte.
    const keep = (pos: number, days: number) => (pos === daysToPos(days) ? days : posToDays(pos));
    const next: [number, number] = activeThumb === 0
      ? [posToDays(value[0]), keep(value[1], curHigh)]
      : [keep(value[0], curLow), posToDays(value[1])];
    // Pelo menos 1 dia entre os dois cortes (o backend exige risco < inativo).
    if (next[0] >= next[1]) {
      if (activeThumb === 0) next[0] = next[1] - 1;
      else next[1] = next[0] + 1;
    }
    next[0] = Math.max(1, next[0]);
    if (next[1] <= next[0]) next[1] = next[0] + 1;
    draftRef.current = next;
    setDraft(next);
  };

  const handleKeyDownCapture = (event: React.KeyboardEvent) => {
    const input = event.target as HTMLElement;
    if (input.getAttribute('type') !== 'range') return;
    const isEdge = event.key === 'Home' || event.key === 'End';
    const step = KEY_STEP_DAYS[event.key];
    if (step === undefined && !isEdge) return;
    // Antes do handler do MUI, que andaria na escala de posição.
    event.preventDefault();
    event.stopPropagation();

    const curLow = Math.min(atRiskDays, inactiveDays);
    const curHigh = Math.max(atRiskDays, inactiveDays);
    const index = Number(input.getAttribute('data-index'));
    // Cada ponto anda entre o limite da régua e o outro ponto, com 1 dia de folga.
    const minDays = index === 0 ? 1 : curLow + 1;
    const maxDays = index === 0 ? curHigh - 1 : Math.max(SLIDER_MAX_DAYS, curHigh);
    if (maxDays < minDays) return;
    const current = index === 0 ? curLow : curHigh;
    const wanted = event.key === 'Home' ? minDays
      : event.key === 'End' ? maxDays
        : current + step * (event.shiftKey && Math.abs(step) === 1 ? 10 : 1);
    const days = Math.min(maxDays, Math.max(minDays, wanted));
    if (days === current) {
      // Estado inválido digitado (risco > inativo): mesmo sem andar, a tecla conserta a ordem.
      if (atRiskDays > inactiveDays) onCommit(curLow, curHigh);
      return;
    }
    onCommit(index === 0 ? days : curLow, index === 0 ? curHigh : days);
  };

  const handleCommit = () => {
    const next = draftRef.current;
    draftRef.current = null;
    setDraft(null);
    if (next && (next[0] !== atRiskDays || next[1] !== inactiveDays)) onCommit(next[0], next[1]);
  };

  // Faixas coloridas no trilho: verde até o risco, âmbar até o inativo, vermelho depois.
  const cutA = daysToPos(low) / 10;
  const cutB = daysToPos(high) / 10;
  const { active: a, at_risk: r, inactive: i } = segments;

  return (
    <Box sx={{ width: '100%' }}>
      {/* pt: espaço pro balão com os dias, que aparece em cima do ponto ao arrastar. */}
      <Box sx={{ px: 1.5, pt: 4 }} onKeyDownCapture={handleKeyDownCapture}>
        <Slider
          value={[daysToPos(low), daysToPos(high)]}
          min={0}
          max={1000}
          step={1}
          marks={MARKS}
          track={false}
          // Sem disableSwap de propósito: com os pontos colados (1 dia), o MUI sempre pega o de
          // risco, que não pode passar do outro, e a régua travava. Deixando cruzar, o arrasto
          // segue e os pontos trocam de papel (ver handleChange).
          onChange={handleChange}
          onChangeCommitted={handleCommit}
          valueLabelDisplay="auto"
          // Dias de verdade (não a posição na régua), também pro leitor de tela.
          valueLabelFormat={(_, index) => __t('%{n} dias', { n: index === 0 ? low : high })}
          getAriaLabel={(index) => (index === 0 ? 'Dias para entrar em risco' : 'Dias para ficar inativo')}
          getAriaValueText={(_, index) => __t('%{n} dias', { n: index === 0 ? low : high })}
          sx={{
            height: 10,
            '& .MuiSlider-rail': {
              height: 10,
              opacity: 1,
              background: `linear-gradient(to right, ${a.color} 0%, ${a.color} ${cutA}%, ${r.color} ${cutA}%, ${r.color} ${cutB}%, ${i.color} ${cutB}%, ${i.color} 100%)`,
            },
            '& .MuiSlider-mark': { display: 'none' },
            '& .MuiSlider-markLabel': { fontSize: 12, color: 'text.secondary', top: 34 },
            '& .MuiSlider-thumb': {
              width: 24, height: 24, bgcolor: 'background.paper', border: '4px solid',
              boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
            },
            '& .MuiSlider-thumb[data-index="0"]': { borderColor: r.color },
            '& .MuiSlider-thumb[data-index="1"]': { borderColor: i.color },
          }}
        />
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 4, rowGap: 1.5, mt: 2 }}>
        <LegendItem label={a.label} color={a.color}>
          <LegendText>{`até ${risk} dias`}</LegendText>
        </LegendItem>
        <LegendItem label={r.label} color={r.color}>
          <LegendText>a partir de</LegendText>
          <DayInput value={risk} color={r.color} ariaLabel="Dias para entrar em risco" onCommit={(days) => onCommit(days, inactive)} />
          <LegendText>dias</LegendText>
        </LegendItem>
        <LegendItem label={i.label} color={i.color}>
          <LegendText>a partir de</LegendText>
          <DayInput value={inactive} color={i.color} ariaLabel="Dias para ficar inativo" onCommit={(days) => onCommit(risk, days)} />
          <LegendText>dias</LegendText>
        </LegendItem>
      </Box>
    </Box>
  );
};

export default LifecycleRangeSlider;
